import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { useSocket } from '../../context/socket';
import {
  HITLAB_SESSION_MESSAGE,
  buildHitLabAuthConfig,
  getHitLabApiMessage,
  isHitLabClientEnabled,
} from './hitLabUtils';
import HitLabRadioCatalogManager from './HitLabRadioCatalogManager';
import HitLabRadioProgrammingMessageManager from './HitLabRadioProgrammingMessageManager';
import './hitLab.css';

const RIGHTS_SCREEN_STATUS = 'uploaded_pending_rights_screen';

const RIGHTS_SCREEN_NEXT_STATUSES = [
  'ready_for_curator_listen',
  'rejected',
  'hold',
];

const EDITORIAL_STATUSES = [
  'ready_for_curator_listen',
  'rejected',
  'hold',
  'approved_members_rotation',
  'hitlab_candidate',
  'street_drop_candidate',
];

const STATUS_LABELS = {
  uploaded_pending_rights_screen: 'Needs rights screen',
  ready_for_curator_listen: 'Ready for curator listen',
  rejected: 'Rejected',
  hold: 'Hold',
  approved_members_rotation: 'Radio rotation approved',
  hitlab_candidate: 'HitLab candidate',
  street_drop_candidate: 'Street Drop candidate',
};

const FUNNEL_LABELS = {
  hitlab_landing_viewed: 'Landing views',
  hitlab_landing_radio_preview_play_clicked: 'Preview play clicks',
  hitlab_start_artist_intake_clicked: 'Start clicks',
  hitlab_auth_started: 'Auth starts',
  hitlab_intake_viewed: 'Auth completed / intake viewed',
  hitlab_artist_profile_completed: 'Artist profile completed',
  hitlab_checkout_started: 'Checkout started',
  hitlab_checkout_canceled: 'Checkout canceled',
  hitlab_checkout_returned_success: 'Checkout success returned',
  hitlab_payment_confirmed: 'Payment confirmed',
  hitlab_submit_viewed_paid: 'Paid submit viewed',
  hitlab_song_submission_completed: 'Song submissions',
};

const FUNNEL_ORDER = Object.keys(FUNNEL_LABELS);

const LISTEN_RESULT_ELIGIBLE_STATUSES = new Set([
  'uploaded_pending_rights_screen',
  ...EDITORIAL_STATUSES,
]);

const LISTEN_RESULT_DECISION_OPTIONS = [
  ['earned_next_move', 'Earned Next Move'],
  ['not_yet', 'Not Yet'],
  ['hold_second_listen', 'Hold / Second Listen'],
];

const LISTEN_RESULT_PRIMARY_LANE_OPTIONS = [
  ['radio_rotation_candidate', 'Radio Rotation Candidate'],
  ['hit_radar_candidate', 'Hit Radar Candidate'],
  ['needs_stronger_hook', 'Needs stronger hook'],
  ['needs_better_mix_master', 'Needs better mix/master'],
  ['needs_clearer_identity', 'Needs clearer identity'],
  ['needs_stronger_performance', 'Needs stronger performance'],
  ['rights_submission_issue', 'Rights/submission issue'],
  ['not_hitlab_fit', 'Not a HitLab fit'],
];

const LISTEN_RESULT_CTA_OPTIONS = [
  ['watch_for_radio_launch', 'Watch for Joe Galaxy Radio launch'],
  ['confirm_rotation_readiness', 'Confirm Rotation Readiness'],
  ['come_back_with_stronger_record', 'Come back with a stronger record'],
  ['localops_earning_lane', 'JG LocalOps earning lane'],
  ['hold_for_second_listen', 'Hold for second listen'],
];

const LISTEN_RESULT_SCORE_FIELDS = [
  ['hook', 'Hook'],
  ['artistIdentity', 'Artist identity'],
  ['vocalPerformance', 'Vocal / performance'],
  ['productionMix', 'Production / mix'],
  ['replayValue', 'Replay value'],
  ['marketEnergy', 'Market energy'],
  ['fanActionPotential', 'Fan-action potential'],
  ['jgUniverseFit', 'JG Universe fit'],
];

const INITIAL_LISTEN_RESULT_FORM = {
  decision: '',
  primaryLane: '',
  privateLinkWorks: '',
  rightsClearForReview: '',
  scores: LISTEN_RESULT_SCORE_FIELDS.reduce((acc, [key]) => {
    acc[key] = '';
    return acc;
  }, {}),
  bestMomentTimestamp: '',
  strongestThing: '',
  mainLimiter: '',
  oneSentenceTruth: '',
  recommendedNextAction: '',
  ctaType: '',
  internalNotes: '',
};

const INITIAL_RECOVERY_FORM = {
  hoursOld: '1',
  maxAgeDays: '',
  limit: '',
  testEmail: '',
  intakeIds: '',
};

const INITIAL_FOLLOWUP_FORM = {
  subject: '',
  body: '',
};

const FOLLOWUP_CHANNEL_LABELS = {
  email: 'Email',
  sms_copy: 'SMS copy',
  ig_copy: 'IG DM copy',
  call_note: 'Call note',
};

const FOLLOWUP_STATUS_LABELS = {
  sent: 'Sent',
  failed: 'Failed',
  copied: 'Copied',
  note: 'Note',
};

const INITIAL_LISTENER_METRICS = {
  summary: {},
  tracks: [],
  listeners: [],
  recent: [],
};

const LISTENER_METRIC_WINDOW_OPTIONS = [1, 7, 30, 90];

const RECOVERY_SKIP_REASON_LABELS = {
  already_sent: 'Already emailed',
  too_recent: 'Too recent',
  suppressed: 'Suppressed',
  already_paid: 'Paid',
  already_submitted: 'Submitted',
  invalid_email: 'Missing or invalid email',
  missing_artist_name: 'Missing artist name',
  has_song_private_url: 'Has song link',
  has_stripe_payment_intent: 'Has Stripe/payment intent',
  too_old: 'Too old',
};

const getSkipReasonCount = (summary, reasons = []) =>
  reasons.reduce(
    (total, reason) => total + Number(summary?.skipReasons?.[reason] || 0),
    0,
  );

const parseRecoveryIntakeIds = (value = '') =>
  Array.from(
    new Set(
      String(value || '')
        .split(/[\n,]+/)
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  );

const buildRecoveryRequestPayload = (form, { dryRun = false } = {}) => {
  const payload = {
    dryRun,
    hoursOld: Number(form.hoursOld) || 1,
  };
  const maxAgeDays = Number(form.maxAgeDays);
  if (Number.isFinite(maxAgeDays) && maxAgeDays > 0) {
    payload.maxAgeDays = Math.floor(maxAgeDays);
  }
  const intakeIds = parseRecoveryIntakeIds(form.intakeIds);
  if (intakeIds.length) payload.intakeIds = intakeIds;
  const limit = Number(form.limit);
  if (Number.isFinite(limit) && limit > 0) {
    payload.limit = Math.floor(limit);
  }
  return payload;
};

const getRecoveryRunSummary = (data = {}) => {
  if (!data) return '';
  const sent = Number(data.sentCount || 0);
  const eligible = Number(data.eligibleCount || 0);
  const failed = Number(data.failedCount || 0);
  const selected = Number(data.selectedCount || 0);
  if (data.dryRun) {
    return `${eligible} eligible, ${selected} selected, ${Number(
      data.skippedCount || 0,
    )} skipped.`;
  }
  return `${sent} sent, ${failed} failed, ${selected} selected.`;
};

const formatMetricNumber = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatJgkMetric = (value) => `${formatMetricNumber(value)} JGK`;

const formatListenDuration = (value) => {
  const seconds = Math.max(0, Number(value || 0));
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
};

const formatMetricDate = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Never';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getStatusLabel = (status) =>
  STATUS_LABELS[status] ||
  String(status || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeStatus = (status) => String(status || '').trim() || 'unknown';

const getArtistDisplayName = (row) =>
  String(row?.artistName || '').trim() || 'Missing artist name';

const getArtistGreetingName = (row) =>
  String(row?.artistName || '').trim() || 'there';

const getSongDisplayName = (row) =>
  String(row?.songTitle || '').trim() || 'your HitLab submission';

const getCallNoteSongName = (row) =>
  String(row?.songTitle || '').trim() || 'No song yet';

const isValidFollowUpEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const HITLAB_FOLLOWUP_BOUNDARY_LINES = [
  'Payment gets your record processed.',
  'Excellence earns the next move.',
  'Placement cannot be bought.',
];

const HITLAB_FOLLOWUP_BOUNDARY_INLINE = HITLAB_FOLLOWUP_BOUNDARY_LINES.join(' ');

const buildInitialFollowUpSubject = (row) =>
  row?.songTitle ? `Follow-up on ${row.songTitle}` : 'JG HitLab follow-up';

const buildInitialFollowUpBody = (row) => {
  const artistIntent = String(row?.artistIntent || '').trim();
  return [
    `Hi ${getArtistGreetingName(row)},`,
    '',
    'I saw your JG Artist Profile come through.',
    '',
    artistIntent ? 'You said:' : '',
    artistIntent ? `"${artistIntent}"` : '',
    artistIntent ? '' : '',
    artistIntent
      ? 'That is exactly the kind of intent JG HitLab was built for.'
      : '',
    artistIntent ? '' : '',
    'The next step is to complete the intake so I can process the record properly.',
    '',
    ...HITLAB_FOLLOWUP_BOUNDARY_LINES,
    '',
    '- Joe Galaxy',
  ]
    .filter((line, index, lines) => line || lines[index - 1])
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');
};

const buildSmsFollowUpCopy = (row) => {
  const artistIntent = String(row?.artistIntent || '').trim();
  return [
    `${getArtistGreetingName(row)} - Joe from JG HitLab. I saw your JG Artist Profile come through.`,
    artistIntent ? `You said: "${artistIntent}"` : '',
    'The next step is to complete the intake so I can process the record properly.',
    HITLAB_FOLLOWUP_BOUNDARY_INLINE,
  ]
    .filter(Boolean)
    .join('\n\n');
};

const buildIgFollowUpCopy = (row) => {
  const artistIntent = String(row?.artistIntent || '').trim();
  return [
    `${getArtistGreetingName(row)} - Joe Galaxy here.`,
    'I saw your JG Artist Profile come through.',
    artistIntent ? `You said: "${artistIntent}"` : '',
    artistIntent
      ? 'That is exactly the kind of intent JG HitLab was built for. The next step is to complete the intake so I can process the record properly.'
      : 'The next step is to complete the intake so I can process the record properly.',
    HITLAB_FOLLOWUP_BOUNDARY_INLINE,
  ]
    .filter(Boolean)
    .join('\n\n');
};

const buildCallNoteCopy = (row) =>
  [
    `Artist: ${getArtistDisplayName(row)}`,
    `Email: ${row?.contactEmail || 'No email on file'}`,
    `Phone: ${row?.contactPhone || 'No phone on file'}`,
    `Contact / Social: ${row?.bestContactOrSocialUrl || 'No social on file'}`,
    `Intent: ${row?.artistIntent || 'No artist intent on file'}`,
    `Status: ${row?.paymentStatus || 'pending'}/${row?.status || 'unknown'}`,
    `Song: ${getCallNoteSongName(row)}`,
    'Goal: complete intake/payment so the record can be processed.',
    'Boundary: Payment gets the record processed. Excellence earns the next move. Placement cannot be bought.',
  ].join('\n');

const formatFollowUpDate = (value) => {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unknown time';
  return date.toLocaleString();
};

const getAllowedAdminStatusOptions = (currentStatus) => {
  const normalizedStatus = normalizeStatus(currentStatus);
  const statuses = new Set([normalizedStatus]);
  if (normalizedStatus === RIGHTS_SCREEN_STATUS) {
    RIGHTS_SCREEN_NEXT_STATUSES.forEach((status) => statuses.add(status));
    return Array.from(statuses);
  }
  if (EDITORIAL_STATUSES.includes(normalizedStatus)) {
    EDITORIAL_STATUSES.forEach((status) => statuses.add(status));
  }
  return Array.from(statuses);
};

const isStatusSelectDisabled = (currentStatus) =>
  getAllowedAdminStatusOptions(currentStatus).length < 2;

const HITLAB_TRANSITION_STALE_MESSAGE =
  'Status could not be updated because that transition is no longer allowed. Refresh and try again.';

const getPlayerRecord = (player) => player?.data?.player || player?.player || player || null;

const canOpenListenResult = (row) =>
  row?.paymentStatus === 'paid' &&
  !!row?.songPrivateUrl &&
  LISTEN_RESULT_ELIGIBLE_STATUSES.has(normalizeStatus(row?.status));

const getListenResultFormFromPayload = (listenResult = {}) => ({
  ...INITIAL_LISTEN_RESULT_FORM,
  decision: listenResult.decision || '',
  primaryLane: listenResult.primaryLane || '',
  privateLinkWorks: listenResult.privateLinkWorks || '',
  rightsClearForReview: listenResult.rightsClearForReview || '',
  scores: LISTEN_RESULT_SCORE_FIELDS.reduce((acc, [key]) => {
    acc[key] = listenResult.scores?.[key] ?? '';
    return acc;
  }, {}),
  bestMomentTimestamp: listenResult.bestMomentTimestamp || '',
  strongestThing: listenResult.strongestThing || '',
  mainLimiter: listenResult.mainLimiter || '',
  oneSentenceTruth: listenResult.oneSentenceTruth || '',
  recommendedNextAction: listenResult.recommendedNextAction || '',
  ctaType: listenResult.ctaType || '',
  internalNotes: listenResult.internalNotes || '',
});

export default function HitLabAdmin() {
  const { player } = useContext(AuthContext);
  const { socket } = useSocket() || {};
  const record = getPlayerRecord(player);
  const isAdmin = String(record?.role || '').trim() === 'admin';
  const authConfig = useMemo(
    () => buildHitLabAuthConfig(player),
    [player?.token],
  );
  const [rows, setRows] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshPromptVisible, setRefreshPromptVisible] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [listenPanelOpen, setListenPanelOpen] = useState(false);
  const [listenResultRow, setListenResultRow] = useState(null);
  const [listenForm, setListenForm] = useState(INITIAL_LISTEN_RESULT_FORM);
  const [listenDraft, setListenDraft] = useState({ subject: '', body: '' });
  const [listenLoading, setListenLoading] = useState(false);
  const [listenSaving, setListenSaving] = useState(false);
  const [listenError, setListenError] = useState('');
  const [listenMessage, setListenMessage] = useState('');
  const [followUpPanelOpen, setFollowUpPanelOpen] = useState(false);
  const [followUpRow, setFollowUpRow] = useState(null);
  const [followUpForm, setFollowUpForm] = useState(INITIAL_FOLLOWUP_FORM);
  const [followUpCallNote, setFollowUpCallNote] = useState('');
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [followUpSending, setFollowUpSending] = useState(false);
  const [followUpCopying, setFollowUpCopying] = useState('');
  const [followUpError, setFollowUpError] = useState('');
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [recoverySummary, setRecoverySummary] = useState(null);
  const [recoveryForm, setRecoveryForm] = useState(INITIAL_RECOVERY_FORM);
  const [recoveryResult, setRecoveryResult] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [listenerMetrics, setListenerMetrics] = useState(INITIAL_LISTENER_METRICS);
  const [listenerMetricsWindowDays, setListenerMetricsWindowDays] = useState('7');
  const [listenerMetricsLoading, setListenerMetricsLoading] = useState(false);
  const [listenerMetricsError, setListenerMetricsError] = useState('');
  const focusRefreshTimerRef = useRef(null);
  const activeStatusEditRef = useRef(false);
  const statusUpdatingRef = useRef(false);
  const pendingInvalidationWhileHiddenRef = useRef(false);
  const followUpPanelRef = useRef(null);
  const shouldScrollFollowUpPanelRef = useRef(false);
  const clientEnabled = isHitLabClientEnabled();

  const scrollFollowUpPanelIntoView = useCallback(() => {
    const panel = followUpPanelRef.current;
    if (!panel || typeof panel.scrollIntoView !== 'function') return;
    const reduceMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    panel.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, []);

  const hasActiveStatusWork = useCallback(
    () => activeStatusEditRef.current || statusUpdatingRef.current,
    [],
  );

  const loadRows = useCallback(() => {
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return Promise.resolve();
    }
    return axiosConn.get('hitlab/admin/intakes', authConfig).then((response) => {
      setRows(response.data?.data || []);
    });
  }, [authConfig]);

  const loadFunnel = useCallback(() => {
    if (!authConfig) return Promise.resolve();
    return axiosConn.get('hitlab/admin/funnel', authConfig).then((response) => {
      setFunnel(response.data?.data || null);
    });
  }, [authConfig]);

  const loadRecoverySummary = useCallback(() => {
    if (!authConfig) return Promise.resolve();
    return axiosConn
      .get('hitlab/admin/recovery/pending-payment-email/summary', authConfig)
      .then((response) => {
        setRecoverySummary(response.data?.data || null);
      });
  }, [authConfig]);

  const loadListenerMetrics = useCallback(() => {
    if (!authConfig) return Promise.resolve();
    setListenerMetricsLoading(true);
    setListenerMetricsError('');
    return axiosConn
      .get('hitlab/admin/radio/listener-metrics', {
        ...authConfig,
        params: {
          windowDays: listenerMetricsWindowDays,
        },
      })
      .then((response) => {
        setListenerMetrics(response.data?.data || INITIAL_LISTENER_METRICS);
      })
      .catch((err) => {
        setListenerMetricsError(
          getHitLabApiMessage(err, 'Radio listener metrics could not be loaded.'),
        );
      })
      .finally(() => {
        setListenerMetricsLoading(false);
      });
  }, [authConfig, listenerMetricsWindowDays]);

  const refreshAdminData = useCallback(
    async (reason = 'manual refresh') => {
      if (!authConfig) {
        setError(HITLAB_SESSION_MESSAGE);
        return;
      }
      setError('');
      try {
        await loadRows();
        await loadFunnel().catch(() => {
          // Funnel summary is useful but must not block the admin queue.
        });
        await loadRecoverySummary().catch(() => {
          // Recovery summary is operational context and should not block queue work.
        });
        await loadListenerMetrics().catch(() => {
          // Listener evidence is useful but must not block queue work.
        });
        pendingInvalidationWhileHiddenRef.current = false;
        setRefreshPromptVisible(false);
      } catch (err) {
        setError(getHitLabApiMessage(err, 'HitLab admin queue could not be loaded.'));
      }
    },
    [authConfig, loadFunnel, loadListenerMetrics, loadRecoverySummary, loadRows],
  );

  useEffect(() => {
    if (!clientEnabled || !isAdmin) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    refreshAdminData('initial load').finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [clientEnabled, isAdmin, refreshAdminData]);

  useEffect(() => {
    if (!clientEnabled || !isAdmin || !authConfig || !socket?.on || !socket?.off || !socket?.emit) {
      return undefined;
    }

    let active = true;
    const subscribe = () => {
      if (!socket.connected) return;
      socket.emit('hitlab:admin:subscribe', {}, () => {});
    };
    const handleInvalidated = () => {
      if (!active) return;
      setRefreshPromptVisible(true);
      if (typeof document !== 'undefined' && document.hidden) {
        pendingInvalidationWhileHiddenRef.current = true;
      }
    };
    const handleConnect = () => subscribe();

    socket.on('connect', handleConnect);
    socket.on('hitlab:admin:invalidated', handleInvalidated);
    subscribe();

    return () => {
      active = false;
      socket.off('connect', handleConnect);
      socket.off('hitlab:admin:invalidated', handleInvalidated);
    };
  }, [authConfig, clientEnabled, isAdmin, socket]);

  useEffect(() => {
    if (!followUpPanelOpen || !shouldScrollFollowUpPanelRef.current) return;
    shouldScrollFollowUpPanelRef.current = false;
    scrollFollowUpPanelIntoView();
  }, [followUpPanelOpen, followUpRow, scrollFollowUpPanelIntoView]);

  useEffect(() => {
    if (!clientEnabled || !isAdmin || !authConfig) return undefined;
    const scheduleRefresh = (reason = 'tab focus') => {
      if (typeof document !== 'undefined' && document.hidden) return;
      window.clearTimeout(focusRefreshTimerRef.current);
      focusRefreshTimerRef.current = window.setTimeout(() => {
        if (hasActiveStatusWork()) {
          setRefreshPromptVisible(true);
          return;
        }
        void refreshAdminData(reason);
      }, 900);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      scheduleRefresh(
        pendingInvalidationWhileHiddenRef.current
          ? 'hidden socket invalidation'
          : 'tab focus',
      );
    };
    const handleWindowFocus = () => {
      scheduleRefresh(
        pendingInvalidationWhileHiddenRef.current
          ? 'hidden socket invalidation'
          : 'tab focus',
      );
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.clearTimeout(focusRefreshTimerRef.current);
    };
  }, [
    authConfig,
    clientEnabled,
    hasActiveStatusWork,
    isAdmin,
    refreshAdminData,
  ]);

  const updateStatus = async (row, status) => {
    const currentStatus = normalizeStatus(row?.status);
    if (status === currentStatus) return;
    setError('');
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return;
    }
    statusUpdatingRef.current = true;
    setStatusUpdating(true);
    try {
      await axiosConn.patch(`hitlab/admin/intakes/${row.id}/status`, {
        status,
      }, authConfig);
      await refreshAdminData('status update');
    } catch (err) {
      if (Number(err?.response?.status) === 400) {
        setError(HITLAB_TRANSITION_STALE_MESSAGE);
        return;
      }
      setError(getHitLabApiMessage(err, 'Status could not be updated.'));
    } finally {
      statusUpdatingRef.current = false;
      setStatusUpdating(false);
    }
  };

  const openListenResult = async (row) => {
    if (!canOpenListenResult(row)) return;
    setListenPanelOpen(true);
    setListenResultRow(row);
    setListenForm(INITIAL_LISTEN_RESULT_FORM);
    setListenDraft({ subject: '', body: '' });
    setListenError('');
    setListenMessage('');
    if (!authConfig) {
      setListenError(HITLAB_SESSION_MESSAGE);
      return;
    }
    setListenLoading(true);
    try {
      const response = await axiosConn.get(
        `hitlab/admin/intakes/${row.id}/listen-result`,
        authConfig,
      );
      const listenResult = response.data?.data?.listenResult || {};
      setListenForm(getListenResultFormFromPayload(listenResult));
      setListenDraft({
        subject: listenResult.emailSubjectDraft || '',
        body: listenResult.emailBodyDraft || '',
      });
    } catch (err) {
      setListenError(
        getHitLabApiMessage(err, 'Listen Result could not be loaded.'),
      );
    } finally {
      setListenLoading(false);
    }
  };

  const closeListenResult = () => {
    setListenPanelOpen(false);
    setListenResultRow(null);
    setListenForm(INITIAL_LISTEN_RESULT_FORM);
    setListenDraft({ subject: '', body: '' });
    setListenError('');
    setListenMessage('');
  };

  const updateListenField = (event) => {
    const { name, value } = event.target;
    setListenForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const updateListenScore = (key, value) => {
    setListenForm((current) => ({
      ...current,
      scores: {
        ...current.scores,
        [key]: value,
      },
    }));
  };

  const generateListenDraft = async (event) => {
    event.preventDefault();
    setListenError('');
    setListenMessage('');
    if (!authConfig) {
      setListenError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!listenResultRow?.id) {
      setListenError('Choose a HitLab intake before generating a Listen Result.');
      return;
    }
    setListenSaving(true);
    try {
      const response = await axiosConn.post(
        `hitlab/admin/intakes/${listenResultRow.id}/listen-result/draft`,
        listenForm,
        authConfig,
      );
      const listenResult = response.data?.data?.listenResult || {};
      setListenForm(getListenResultFormFromPayload(listenResult));
      setListenDraft({
        subject: listenResult.emailSubjectDraft || '',
        body: listenResult.emailBodyDraft || '',
      });
      setListenMessage('Listen Result draft saved.');
    } catch (err) {
      setListenError(
        getHitLabApiMessage(err, 'Listen Result draft could not be generated.'),
      );
    } finally {
      setListenSaving(false);
    }
  };

  const copyListenDraft = async () => {
    setListenMessage('');
    setListenError('');
    const copyText = [
      listenDraft.subject ? `Subject: ${listenDraft.subject}` : '',
      listenDraft.body,
    ].filter(Boolean).join('\n\n');
    if (!copyText.trim()) {
      setListenError('Generate a Listen Result draft before copying.');
      return;
    }
    try {
      if (!navigator.clipboard?.writeText) {
        setListenMessage('Select the draft text and copy it manually.');
        return;
      }
      await navigator.clipboard.writeText(copyText);
      setListenMessage('Email draft copied.');
    } catch (err) {
      setListenMessage('Select the draft text and copy it manually.');
    }
  };

  const loadFollowUpHistory = async (row = followUpRow) => {
    if (!authConfig || !row?.id) return;
    setFollowUpLoading(true);
    try {
      const response = await axiosConn.get(
        `hitlab/admin/intakes/${row.id}/followups`,
        authConfig,
      );
      setFollowUpHistory(response.data?.data || []);
    } catch (err) {
      setFollowUpError(
        getHitLabApiMessage(err, 'Follow-up history could not be loaded.'),
      );
    } finally {
      setFollowUpLoading(false);
    }
  };

  const openFollowUp = async (row) => {
    setListenPanelOpen(false);
    shouldScrollFollowUpPanelRef.current = true;
    setFollowUpPanelOpen(true);
    setFollowUpRow(row);
    setFollowUpForm({
      subject: buildInitialFollowUpSubject(row),
      body: buildInitialFollowUpBody(row),
    });
    setFollowUpCallNote(buildCallNoteCopy(row));
    setFollowUpHistory([]);
    setFollowUpError('');
    setFollowUpMessage('');
    if (!authConfig) {
      setFollowUpError(HITLAB_SESSION_MESSAGE);
      return;
    }
    await loadFollowUpHistory(row);
  };

  const closeFollowUp = () => {
    setFollowUpPanelOpen(false);
    setFollowUpRow(null);
    setFollowUpForm(INITIAL_FOLLOWUP_FORM);
    setFollowUpCallNote('');
    setFollowUpHistory([]);
    setFollowUpError('');
    setFollowUpMessage('');
    setFollowUpCopying('');
  };

  const updateFollowUpField = (event) => {
    const { name, value } = event.target;
    setFollowUpForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const sendFollowUpEmail = async (event) => {
    event.preventDefault();
    setFollowUpError('');
    setFollowUpMessage('');
    if (!authConfig) {
      setFollowUpError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!followUpRow?.id) {
      setFollowUpError('Choose a HitLab intake before sending follow-up.');
      return;
    }
    if (!isValidFollowUpEmail(followUpRow.contactEmail)) {
      setFollowUpError('Artist contact email is missing or invalid.');
      return;
    }
    setFollowUpSending(true);
    try {
      await axiosConn.post(
        `hitlab/admin/intakes/${followUpRow.id}/followups/email`,
        followUpForm,
        authConfig,
      );
      setFollowUpMessage('Follow-up email sent.');
      await loadFollowUpHistory(followUpRow);
    } catch (err) {
      setFollowUpError(
        getHitLabApiMessage(err, 'Follow-up email could not be sent.'),
      );
    } finally {
      setFollowUpSending(false);
    }
  };

  const copyFollowUpText = async (channel) => {
    setFollowUpError('');
    setFollowUpMessage('');
    if (!authConfig) {
      setFollowUpError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!followUpRow?.id) {
      setFollowUpError('Choose a HitLab intake before copying follow-up.');
      return;
    }
    const copyText =
      channel === 'sms_copy'
        ? buildSmsFollowUpCopy(followUpRow)
        : channel === 'ig_copy'
        ? buildIgFollowUpCopy(followUpRow)
        : followUpCallNote;
    if (!String(copyText || '').trim()) {
      setFollowUpError('Follow-up copy is required.');
      return;
    }
    try {
      if (!navigator.clipboard?.writeText) {
        setFollowUpMessage('Select the text and copy it manually.');
        return;
      }
      setFollowUpCopying(channel);
      await navigator.clipboard.writeText(copyText);
      await axiosConn.post(
        `hitlab/admin/intakes/${followUpRow.id}/followups/manual-note`,
        {
          channel,
          body: copyText,
        },
        authConfig,
      );
      setFollowUpMessage(`${FOLLOWUP_CHANNEL_LABELS[channel]} saved.`);
      await loadFollowUpHistory(followUpRow);
    } catch (err) {
      setFollowUpError(
        getHitLabApiMessage(err, 'Follow-up copy could not be saved.'),
      );
    } finally {
      setFollowUpCopying('');
    }
  };

  const updateRecoveryField = (event) => {
    const { name, value } = event.target;
    setRecoveryForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const runRecoveryDryRun = async () => {
    setRecoveryError('');
    setRecoveryResult('');
    if (!authConfig) {
      setRecoveryError(HITLAB_SESSION_MESSAGE);
      return;
    }
    setRecoveryBusy(true);
    try {
      const payload = buildRecoveryRequestPayload(recoveryForm, { dryRun: true });
      const response = await axiosConn.post(
        'hitlab/admin/recovery/pending-payment-email/run',
        payload,
        authConfig,
      );
      const data = response.data?.data || {};
      setRecoveryResult(`Preview unsent: ${getRecoveryRunSummary(data)}`);
      setRecoverySummary((current) => ({
        ...(current || {}),
        ...data,
      }));
    } catch (err) {
      setRecoveryError(
        getHitLabApiMessage(err, 'Recovery dry run could not be completed.'),
      );
    } finally {
      setRecoveryBusy(false);
    }
  };

  const runRecoverySend = async () => {
    setRecoveryError('');
    setRecoveryResult('');
    if (!authConfig) {
      setRecoveryError(HITLAB_SESSION_MESSAGE);
      return;
    }
    const payload = buildRecoveryRequestPayload(recoveryForm, { dryRun: false });
    const targetedCount = Array.isArray(payload.intakeIds)
      ? payload.intakeIds.length
      : 0;
    const confirmCopy = payload.limit || targetedCount
      ? `Send HitLab recovery email to ${payload.limit || targetedCount} selected eligible intake(s)?`
      : 'Send HitLab recovery email to all eligible unsent pending-payment profiles?';
    if (!window.confirm(confirmCopy)) return;

    setRecoveryBusy(true);
    try {
      const response = await axiosConn.post(
        'hitlab/admin/recovery/pending-payment-email/run',
        payload,
        authConfig,
      );
      const data = response.data?.data || {};
      setRecoveryResult(`Run complete: ${getRecoveryRunSummary(data)}`);
      await loadRecoverySummary().catch(() => {});
    } catch (err) {
      setRecoveryError(
        getHitLabApiMessage(err, 'Recovery email run could not be completed.'),
      );
    } finally {
      setRecoveryBusy(false);
    }
  };

  const sendRecoveryTestEmail = async () => {
    setRecoveryError('');
    setRecoveryResult('');
    if (!authConfig) {
      setRecoveryError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!String(recoveryForm.testEmail || '').trim()) {
      setRecoveryError('Test email is required.');
      return;
    }
    setRecoveryBusy(true);
    try {
      const response = await axiosConn.post(
        'hitlab/admin/recovery/pending-payment-email/test-send',
        { testEmail: recoveryForm.testEmail },
        authConfig,
      );
      const warning = response.data?.data?.warning;
      setRecoveryResult(
        warning
          ? `Test sent. ${warning}`
          : `Test sent to ${recoveryForm.testEmail}.`,
      );
    } catch (err) {
      setRecoveryError(
        getHitLabApiMessage(err, 'Recovery test email could not be sent.'),
      );
    } finally {
      setRecoveryBusy(false);
    }
  };

  const listenerSummary = listenerMetrics?.summary || {};
  const listenerTracks = Array.isArray(listenerMetrics?.tracks)
    ? listenerMetrics.tracks
    : [];
  const listenerRows = Array.isArray(listenerMetrics?.listeners)
    ? listenerMetrics.listeners
    : [];
  const recentListens = Array.isArray(listenerMetrics?.recent)
    ? listenerMetrics.recent
    : [];

  if (!isAdmin) {
    return (
      <main className="hitlab-page hitlab-admin">
        <section className="hitlab-shell">
          <h1>HitLab admin unavailable</h1>
          <p>This queue is restricted to admins.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="hitlab-page hitlab-admin">
      <Helmet>
        <title>JG HitLab Admin Queue</title>
      </Helmet>
      <section className="hitlab-shell">
        <p className="hitlab-kicker">ADMIN QUEUE</p>
        <h1>JG HitLab Artist Intakes</h1>
        {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
        {refreshPromptVisible ? (
          <div className="hitlab-alert hitlab-admin-refresh-prompt">
            <span>HitLab admin has new activity.</span>
            <button
              type="button"
              className="hitlab-secondary-btn"
              disabled={statusUpdating}
              onClick={() => refreshAdminData('manual refresh')}
            >
              Refresh queue
            </button>
          </div>
        ) : null}
        {!clientEnabled ? (
          <div className="hitlab-alert">Artist intake is disabled.</div>
        ) : loading ? (
          <div className="hitlab-alert">Loading queue...</div>
        ) : !authConfig ? (
          null
        ) : (
          <>
            <section className="hitlab-admin-funnel" aria-label="HitLab funnel summary">
              <article className="hitlab-admin-funnel-card hitlab-admin-funnel-card-accent">
                <span>Needs review</span>
                <strong>{Number(funnel?.needsReviewCount || 0)}</strong>
              </article>
              {FUNNEL_ORDER.map((eventName) => (
                <article className="hitlab-admin-funnel-card" key={eventName}>
                  <span>{FUNNEL_LABELS[eventName]}</span>
                  <strong>{Number(funnel?.windows?.allTime?.[eventName] || 0)}</strong>
                  <small>All time</small>
                </article>
              ))}
            </section>

            <section className="hitlab-admin-status-strip" aria-label="HitLab status counts">
              {Object.entries(STATUS_LABELS).map(([status, label]) => (
                <article key={status}>
                  <span>{label}</span>
                  <strong>{Number(funnel?.statusCounts?.[status] || 0)}</strong>
                </article>
              ))}
            </section>

            <section
              className="hitlab-recovery-admin"
              aria-label="HitLab pending payment recovery email"
            >
              <div className="hitlab-recovery-admin-header">
                <div>
                  <p className="hitlab-kicker">PENDING PAYMENT RECOVERY</p>
                  <h2>Profile-ready recovery email</h2>
                  <p>
                    {Number(recoverySummary?.eligibleCount || 0)} eligible now -{' '}
                    {Number(recoverySummary?.sentCount || 0)} already emailed
                  </p>
                  <p>
                    Send the current recovery email to profile-ready artists who
                    have not paid and have not already received it.
                  </p>
                </div>
                <button
                  type="button"
                  className="hitlab-secondary-btn"
                  disabled={recoveryBusy}
                  onClick={loadRecoverySummary}
                >
                  Refresh summary
                </button>
              </div>

              {recoveryError ? (
                <div className="hitlab-alert hitlab-alert-error">{recoveryError}</div>
              ) : null}
              {recoveryResult ? (
                <div className="hitlab-alert hitlab-alert-success">{recoveryResult}</div>
              ) : null}

              <div className="hitlab-recovery-metrics">
                <article>
                  <span>Eligible now</span>
                  <strong>{Number(recoverySummary?.eligibleCount || 0)}</strong>
                </article>
                <article>
                  <span>Already emailed</span>
                  <strong>
                    {getSkipReasonCount(recoverySummary, ['already_sent'])}
                  </strong>
                </article>
                <article>
                  <span>Too recent</span>
                  <strong>{getSkipReasonCount(recoverySummary, ['too_recent'])}</strong>
                </article>
                <article>
                  <span>Suppressed</span>
                  <strong>{getSkipReasonCount(recoverySummary, ['suppressed'])}</strong>
                </article>
                <article>
                  <span>Paid/submitted</span>
                  <strong>
                    {getSkipReasonCount(recoverySummary, [
                      'already_paid',
                      'already_submitted',
                    ])}
                  </strong>
                </article>
                <article>
                  <span>Missing email</span>
                  <strong>
                    {getSkipReasonCount(recoverySummary, [
                      'invalid_email',
                      'missing_artist_name',
                    ])}
                  </strong>
                </article>
                <article>
                  <span>Failed</span>
                  <strong>{Number(recoverySummary?.failedCount || 0)}</strong>
                </article>
                <article>
                  <span>Address</span>
                  <strong>
                    {recoverySummary?.postalAddress?.configured ? 'Ready' : 'Blocked'}
                  </strong>
                </article>
              </div>

              {Object.values(recoverySummary?.skipReasons || {}).some(Boolean) ? (
                <div className="hitlab-recovery-skip-breakdown">
                  <span>Skipped breakdown</span>
                  <ul>
                    {Object.entries(recoverySummary.skipReasons).map(
                      ([reason, count]) =>
                        Number(count || 0) > 0 ? (
                          <li key={reason}>
                            <strong>
                              {RECOVERY_SKIP_REASON_LABELS[reason] ||
                                reason.replace(/_/g, ' ')}
                            </strong>
                            <span>{Number(count || 0)}</span>
                          </li>
                        ) : null,
                    )}
                  </ul>
                </div>
              ) : null}

              <div className="hitlab-recovery-grid">
                <div className="hitlab-recovery-controls">
                  <label>
                    Wait after profile ready
                    <input
                      type="number"
                      name="hoursOld"
                      min="1"
                      step="1"
                      value={recoveryForm.hoursOld}
                      onChange={updateRecoveryField}
                    />
                    <small>
                      Only send after the artist profile has been ready for this
                      long.
                    </small>
                  </label>
                  <label>
                    Safety limit
                    <input
                      type="number"
                      name="limit"
                      min="1"
                      step="1"
                      placeholder="All eligible"
                      value={recoveryForm.limit}
                      onChange={updateRecoveryField}
                    />
                    <small>
                      Leave blank to send every eligible unsent profile in this
                      preview/run.
                    </small>
                  </label>
                  <label>
                    Test email
                    <input
                      type="email"
                      name="testEmail"
                      placeholder="admin@example.com"
                      value={recoveryForm.testEmail}
                      onChange={updateRecoveryField}
                    />
                  </label>
                  <details className="hitlab-recovery-advanced hitlab-form-wide">
                    <summary>Advanced</summary>
                    <label>
                      Optional cutoff
                      <input
                        type="number"
                        name="maxAgeDays"
                        min="1"
                        step="1"
                        placeholder="No max-age cutoff"
                        value={recoveryForm.maxAgeDays}
                        onChange={updateRecoveryField}
                      />
                      <small>
                        Leave blank to include all unsent profile-ready artists.
                      </small>
                    </label>
                  </details>
                  <label className="hitlab-form-wide">
                    Targeted intake IDs
                    <textarea
                      name="intakeIds"
                      placeholder="Comma or newline separated"
                      value={recoveryForm.intakeIds}
                      onChange={updateRecoveryField}
                    />
                  </label>
                </div>

                <div className="hitlab-recovery-actions">
                  <button
                    type="button"
                    className="hitlab-secondary-btn"
                    disabled={recoveryBusy}
                    onClick={runRecoveryDryRun}
                  >
                    Preview unsent
                  </button>
                  <button
                    type="button"
                    className="hitlab-secondary-btn"
                    disabled={recoveryBusy}
                    onClick={sendRecoveryTestEmail}
                  >
                    Send test
                  </button>
                  <button
                    type="button"
                    className="hitlab-primary-btn"
                    disabled={recoveryBusy}
                    onClick={runRecoverySend}
                  >
                    {recoveryBusy ? 'Working...' : 'Send to all unsent now'}
                  </button>
                  {!recoveryForm.limit && !parseRecoveryIntakeIds(recoveryForm.intakeIds).length ? (
                    <p>
                      Send to all unsent now sends to every currently eligible
                      unsent profile.
                    </p>
                  ) : null}
                </div>
              </div>
            </section>

            <section
              className="hitlab-radio-listener-metrics"
              aria-label="JG Radio listener metrics"
            >
              <div className="hitlab-radio-listener-metrics-header">
                <div>
                  <p className="hitlab-kicker">RADIO LISTENER METRICS</p>
                  <h2>JG Radio listening evidence</h2>
                  <p>
                    Starts, qualified listens, known listeners, feedback, and
                    support activity from real Radio playback.
                  </p>
                </div>
                <div className="hitlab-radio-listener-actions">
                  <label>
                    Window
                    <select
                      value={listenerMetricsWindowDays}
                      onChange={(event) =>
                        setListenerMetricsWindowDays(event.target.value)
                      }
                    >
                      {LISTENER_METRIC_WINDOW_OPTIONS.map((days) => (
                        <option key={days} value={String(days)}>
                          {days} day{days === 1 ? '' : 's'}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="hitlab-secondary-btn"
                    disabled={listenerMetricsLoading}
                    onClick={loadListenerMetrics}
                  >
                    {listenerMetricsLoading ? 'Loading...' : 'Refresh metrics'}
                  </button>
                </div>
              </div>

              {listenerMetricsError ? (
                <div className="hitlab-alert hitlab-alert-error">
                  {listenerMetricsError}
                </div>
              ) : null}

              <div className="hitlab-radio-listener-summary">
                <article>
                  <span>Starts</span>
                  <strong>{formatMetricNumber(listenerSummary.totalStarts)}</strong>
                </article>
                <article>
                  <span>Qualified listens</span>
                  <strong>
                    {formatMetricNumber(listenerSummary.totalQualifiedListens)}
                  </strong>
                </article>
                <article>
                  <span>Known listeners</span>
                  <strong>{formatMetricNumber(listenerSummary.knownListeners)}</strong>
                </article>
                <article>
                  <span>Anonymous sessions</span>
                  <strong>
                    {formatMetricNumber(listenerSummary.anonymousSessions)}
                  </strong>
                </article>
                <article>
                  <span>Repeat known</span>
                  <strong>
                    {formatMetricNumber(listenerSummary.repeatKnownListeners)}
                  </strong>
                </article>
                <article>
                  <span>Listen time</span>
                  <strong>
                    {formatListenDuration(listenerSummary.totalListenedSeconds)}
                  </strong>
                </article>
                <article>
                  <span>Feedback</span>
                  <strong>{formatMetricNumber(listenerSummary.feedbackCount)}</strong>
                </article>
                <article>
                  <span>Support gross</span>
                  <strong>{formatJgkMetric(listenerSummary.supportJgkGross)}</strong>
                </article>
              </div>

              <div className="hitlab-radio-listener-grid">
                <section aria-label="Radio listener metrics by track">
                  <h3>Records</h3>
                  {listenerTracks.length ? (
                    <div className="hitlab-radio-listener-table-wrap">
                      <table className="hitlab-radio-listener-table">
                        <thead>
                          <tr>
                            <th>Record</th>
                            <th>Starts</th>
                            <th>Qualified</th>
                            <th>Known</th>
                            <th>Anonymous</th>
                            <th>Time</th>
                            <th>Signal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listenerTracks.slice(0, 10).map((track) => (
                            <tr key={track.trackId}>
                              <td>
                                <strong>{track.title || track.trackId}</strong>
                                <span>{track.artistName || 'JG Radio'}</span>
                              </td>
                              <td>{formatMetricNumber(track.starts)}</td>
                              <td>{formatMetricNumber(track.qualifiedListens)}</td>
                              <td>{formatMetricNumber(track.uniqueKnownListeners)}</td>
                              <td>{formatMetricNumber(track.anonymousSessions)}</td>
                              <td>{formatListenDuration(track.totalListenedSeconds)}</td>
                              <td>
                                <span>
                                  {formatMetricNumber(track.feedbackCount)} feedback
                                </span>
                                <span>
                                  {formatJgkMetric(track.supportJgkGross)} support
                                </span>
                                <span>
                                  {track.artistConnected ? 'Artist connected' : 'Unmapped'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="hitlab-alert">
                      No listener sessions in this window yet.
                    </div>
                  )}
                </section>

                <section aria-label="Known Radio listeners">
                  <h3>Known listeners</h3>
                  {listenerRows.length ? (
                    <div className="hitlab-radio-listener-stack">
                      {listenerRows.slice(0, 8).map((listener) => (
                        <article key={listener.playerId}>
                          <strong>@{listener.playerId}</strong>
                          <span>
                            {formatMetricNumber(listener.qualifiedListens)} qualified
                            {' / '}
                            {formatListenDuration(listener.totalListenedSeconds)}
                          </span>
                          <span>{listener.topTrackTitle || 'No top record yet'}</span>
                          <small>{formatMetricDate(listener.lastListenedAt)}</small>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="hitlab-alert">
                      No signed-in listeners in this window yet.
                    </div>
                  )}
                </section>
              </div>

              <section aria-label="Recent Radio listens">
                <h3>Recent listens</h3>
                {recentListens.length ? (
                  <div className="hitlab-radio-listener-recent">
                    {recentListens.slice(0, 8).map((listen, index) => (
                      <article key={`${listen.at}-${listen.trackTitle}-${index}`}>
                        <strong>{listen.trackTitle || 'Radio record'}</strong>
                        <span>
                          {listen.artistName || 'JG Radio'} - {listen.playerId}
                        </span>
                        <span>
                          {formatListenDuration(listen.listenedSeconds)}
                          {listen.qualified ? ' qualified' : ' unqualified'}
                        </span>
                        <small>{formatMetricDate(listen.at)}</small>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="hitlab-alert">No recent listens yet.</div>
                )}
              </section>
            </section>

            <HitLabRadioProgrammingMessageManager authConfig={authConfig} />
            <HitLabRadioCatalogManager authConfig={authConfig} />

            <div className="hitlab-table-wrap">
              <table className="hitlab-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Artist</th>
                    <th>Song</th>
                    <th>Genre</th>
                    <th>Paid</th>
                    <th>Rights</th>
                    <th>Listen</th>
                    <th>Result</th>
                    <th>Follow Up</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const currentStatus = normalizeStatus(row.status);
                    const statusOptions = getAllowedAdminStatusOptions(currentStatus);
                    const statusSelectDisabled = isStatusSelectDisabled(currentStatus);
                    const artistDisplayName = getArtistDisplayName(row);
                    return (
                      <tr key={row.id}>
                        <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '--'}</td>
                        <td>
                          <strong>{artistDisplayName}</strong>
                          <span>{row.cityRegion || 'No city yet'}</span>
                          {row.artistIntent ? (
                            <span className="hitlab-table-intent">
                              <b>Artist Intent</b>
                              {row.artistIntent}
                            </span>
                          ) : null}
                        </td>
                        <td>{row.songTitle || 'No song yet'}</td>
                        <td>{row.genreLane || '--'}</td>
                        <td>{row.paymentStatus === 'paid' ? 'Paid' : 'Pending'}</td>
                        <td>
                          {row.rights?.required?.privateReviewPlayback
                            ? 'Confirmed'
                            : 'Pending'}
                        </td>
                        <td>
                          {row.songPrivateUrl ? (
                            <a
                              href={row.songPrivateUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open
                            </a>
                          ) : (
                            '--'
                          )}
                        </td>
                        <td>
                          {canOpenListenResult(row) ? (
                            <>
                              <button
                                type="button"
                                className="hitlab-table-action"
                                onClick={() => openListenResult(row)}
                              >
                                Open Listen Result
                              </button>
                              {row.listenResult?.status === 'drafted' ? (
                                <span>Drafted</span>
                              ) : null}
                            </>
                          ) : (
                            '--'
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="hitlab-table-action"
                            onClick={() => openFollowUp(row)}
                          >
                            Follow Up
                          </button>
                        </td>
                        <td>
                          <select
                            className="hitlab-status-select"
                            aria-label={`Status for ${artistDisplayName || row.id}`}
                            disabled={statusSelectDisabled}
                            value={currentStatus}
                            onFocus={() => {
                              activeStatusEditRef.current = true;
                            }}
                            onBlur={() => {
                              activeStatusEditRef.current = false;
                            }}
                            onChange={(event) => updateStatus(row, event.target.value)}
                          >
                            {statusOptions.map((status) => (
                              <option key={status} value={status}>
                                {getStatusLabel(status)}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                  {!rows.length ? (
                    <tr>
                      <td colSpan="10">No HitLab intakes yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {listenPanelOpen ? (
              <section className="hitlab-listen-panel" aria-label="JG HitLab Listen Result">
                <div className="hitlab-listen-panel-header">
                  <div>
                    <p className="hitlab-kicker">JG HITLAB LISTEN RESULT</p>
                    <h2>{listenResultRow?.artistName || 'HitLab artist'}</h2>
                    <p>
                      {listenResultRow?.songTitle || 'Untitled song'} -{' '}
                      {getStatusLabel(listenResultRow?.status)}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="hitlab-secondary-btn"
                    onClick={closeListenResult}
                  >
                    Close
                  </button>
                </div>
                {listenError ? (
                  <div className="hitlab-alert hitlab-alert-error">{listenError}</div>
                ) : null}
                {listenMessage ? (
                  <div className="hitlab-alert hitlab-alert-success">{listenMessage}</div>
                ) : null}
                {listenLoading ? (
                  <div className="hitlab-alert">Loading Listen Result...</div>
                ) : (
                  <form className="hitlab-listen-form" onSubmit={generateListenDraft}>
                    <div className="hitlab-listen-form-grid">
                      <label>
                        Private link works?
                        <select
                          name="privateLinkWorks"
                          value={listenForm.privateLinkWorks}
                          onChange={updateListenField}
                          required
                        >
                          <option value="">Select</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </label>
                      <label>
                        Rights clear enough for review?
                        <select
                          name="rightsClearForReview"
                          value={listenForm.rightsClearForReview}
                          onChange={updateListenField}
                          required
                        >
                          <option value="">Select</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="concern">Concern</option>
                        </select>
                      </label>
                      <label>
                        Decision
                        <select
                          name="decision"
                          value={listenForm.decision}
                          onChange={updateListenField}
                          required
                        >
                          <option value="">Select</option>
                          {LISTEN_RESULT_DECISION_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Primary lane
                        <select
                          name="primaryLane"
                          value={listenForm.primaryLane}
                          onChange={updateListenField}
                        >
                          <option value="">Backend default</option>
                          {LISTEN_RESULT_PRIMARY_LANE_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        CTA type
                        <select
                          name="ctaType"
                          value={listenForm.ctaType}
                          onChange={updateListenField}
                        >
                          <option value="">Backend default</option>
                          {LISTEN_RESULT_CTA_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <fieldset className="hitlab-listen-scores">
                      <legend>Scores, 1-10</legend>
                      <div className="hitlab-listen-score-grid">
                        {LISTEN_RESULT_SCORE_FIELDS.map(([key, label]) => (
                          <label key={key}>
                            {label}
                            <input
                              type="number"
                              min="1"
                              max="10"
                              step="1"
                              value={listenForm.scores[key]}
                              onChange={(event) => updateListenScore(key, event.target.value)}
                            />
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <div className="hitlab-listen-form-grid">
                      <label>
                        Best moment / timestamp
                        <input
                          name="bestMomentTimestamp"
                          maxLength="80"
                          value={listenForm.bestMomentTimestamp}
                          onChange={updateListenField}
                        />
                      </label>
                      <label>
                        One sentence truth
                        <input
                          name="oneSentenceTruth"
                          maxLength="500"
                          value={listenForm.oneSentenceTruth}
                          onChange={updateListenField}
                        />
                      </label>
                      <label className="hitlab-form-wide">
                        Strongest thing about the record
                        <textarea
                          name="strongestThing"
                          maxLength="1000"
                          value={listenForm.strongestThing}
                          onChange={updateListenField}
                        />
                      </label>
                      <label className="hitlab-form-wide">
                        Main thing holding it back
                        <textarea
                          name="mainLimiter"
                          maxLength="1000"
                          value={listenForm.mainLimiter}
                          onChange={updateListenField}
                        />
                      </label>
                      <label className="hitlab-form-wide">
                        Recommended next action
                        <textarea
                          name="recommendedNextAction"
                          maxLength="1000"
                          value={listenForm.recommendedNextAction}
                          onChange={updateListenField}
                        />
                      </label>
                      <label className="hitlab-form-wide">
                        Internal notes, not sent to artist
                        <textarea
                          name="internalNotes"
                          maxLength="2000"
                          value={listenForm.internalNotes}
                          onChange={updateListenField}
                        />
                      </label>
                    </div>

                    <div className="hitlab-listen-actions">
                      <button
                        type="submit"
                        className="hitlab-primary-btn"
                        disabled={listenSaving}
                      >
                        {listenSaving ? 'Generating...' : 'Generate Listen Result Draft'}
                      </button>
                      <button
                        type="button"
                        className="hitlab-secondary-btn"
                        onClick={copyListenDraft}
                        disabled={!listenDraft.body}
                      >
                        Copy Email Draft
                      </button>
                    </div>
                  </form>
                )}

                {listenDraft.subject || listenDraft.body ? (
                  <div className="hitlab-listen-draft">
                    <label>
                      Email subject draft
                      <input
                        value={listenDraft.subject}
                        onChange={(event) =>
                          setListenDraft((current) => ({
                            ...current,
                            subject: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Email body draft
                      <textarea
                        value={listenDraft.body}
                        onChange={(event) =>
                          setListenDraft((current) => ({
                            ...current,
                            body: event.target.value,
                          }))
                        }
                        onFocus={(event) => event.target.select()}
                      />
                    </label>
                  </div>
                ) : null}
              </section>
            ) : null}
            {followUpPanelOpen ? (
              <section
                ref={followUpPanelRef}
                className="hitlab-listen-panel hitlab-followup-panel"
                aria-label="JG HitLab Artist Follow-Up"
              >
                <div className="hitlab-listen-panel-header">
                  <div>
                    <p className="hitlab-kicker">JG HITLAB ARTIST FOLLOW-UP</p>
                    <h2>{getArtistDisplayName(followUpRow)}</h2>
                    <p>
                      {getSongDisplayName(followUpRow)} -{' '}
                      {getStatusLabel(followUpRow?.status)}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="hitlab-secondary-btn"
                    onClick={closeFollowUp}
                  >
                    Close
                  </button>
                </div>

                {followUpError ? (
                  <div className="hitlab-alert hitlab-alert-error">{followUpError}</div>
                ) : null}
                {followUpMessage ? (
                  <div className="hitlab-alert hitlab-alert-success">
                    {followUpMessage}
                  </div>
                ) : null}

                <div
                  className="hitlab-followup-context"
                  aria-label="Artist contact context"
                >
                  <article>
                    <span>Email</span>
                    <strong>{followUpRow?.contactEmail || 'No email on file'}</strong>
                  </article>
                  <article>
                    <span>Phone</span>
                    <strong>{followUpRow?.contactPhone || 'No phone on file'}</strong>
                  </article>
                  <article>
                    <span>Social</span>
                    <strong>
                      {followUpRow?.bestContactOrSocialUrl || 'No social on file'}
                    </strong>
                  </article>
                  <article>
                    <span>Payment / Status</span>
                    <strong>
                      {followUpRow?.paymentStatus || 'pending'} /{' '}
                      {getStatusLabel(followUpRow?.status)}
                    </strong>
                  </article>
                  <article>
                    <span>Genre</span>
                    <strong>{followUpRow?.genreLane || 'No genre yet'}</strong>
                  </article>
                  <article className="hitlab-followup-context-wide">
                    <span>Artist Intent</span>
                    <strong>
                      {followUpRow?.artistIntent || 'No artist intent on file'}
                    </strong>
                  </article>
                </div>

                {!isValidFollowUpEmail(followUpRow?.contactEmail) ? (
                  <div className="hitlab-alert">
                    Artist contact email is missing or invalid.
                  </div>
                ) : null}

                <form
                  className="hitlab-listen-form hitlab-followup-form"
                  onSubmit={sendFollowUpEmail}
                >
                  <label>
                    Subject
                    <input
                      name="subject"
                      maxLength="160"
                      value={followUpForm.subject}
                      onChange={updateFollowUpField}
                      required
                    />
                  </label>
                  <label>
                    Body
                    <textarea
                      name="body"
                      maxLength="8000"
                      value={followUpForm.body}
                      onChange={updateFollowUpField}
                      required
                    />
                  </label>
                  <div className="hitlab-listen-actions">
                    <button
                      type="submit"
                      className="hitlab-primary-btn"
                      disabled={
                        followUpSending ||
                        !isValidFollowUpEmail(followUpRow?.contactEmail)
                      }
                    >
                      {followUpSending ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </form>

                <div className="hitlab-followup-copy-grid">
                  <article>
                    <h3>SMS</h3>
                    <textarea
                      readOnly
                      value={buildSmsFollowUpCopy(followUpRow)}
                      onFocus={(event) => event.target.select()}
                    />
                    <button
                      type="button"
                      className="hitlab-secondary-btn"
                      onClick={() => copyFollowUpText('sms_copy')}
                      disabled={followUpCopying === 'sms_copy'}
                    >
                      Copy SMS
                    </button>
                  </article>
                  <article>
                    <h3>IG DM</h3>
                    <textarea
                      readOnly
                      value={buildIgFollowUpCopy(followUpRow)}
                      onFocus={(event) => event.target.select()}
                    />
                    <button
                      type="button"
                      className="hitlab-secondary-btn"
                      onClick={() => copyFollowUpText('ig_copy')}
                      disabled={followUpCopying === 'ig_copy'}
                    >
                      Copy IG DM
                    </button>
                  </article>
                  <article>
                    <h3>Call Notes</h3>
                    <textarea
                      value={followUpCallNote}
                      onChange={(event) => setFollowUpCallNote(event.target.value)}
                      onFocus={(event) => event.target.select()}
                    />
                    <button
                      type="button"
                      className="hitlab-secondary-btn"
                      onClick={() => copyFollowUpText('call_note')}
                      disabled={followUpCopying === 'call_note'}
                    >
                      Copy Call Notes
                    </button>
                  </article>
                </div>

                <section
                  className="hitlab-followup-history"
                  aria-label="Follow-up history"
                >
                  <h3>History</h3>
                  {followUpLoading ? (
                    <div className="hitlab-alert">Loading follow-up history...</div>
                  ) : null}
                  {!followUpLoading && !followUpHistory.length ? (
                    <p>No follow-ups yet.</p>
                  ) : null}
                  {followUpHistory.map((item) => (
                    <article key={item.id || `${item.channel}-${item.createdAt}`}>
                      <div>
                        <strong>
                          {FOLLOWUP_CHANNEL_LABELS[item.channel] || item.channel} -{' '}
                          {FOLLOWUP_STATUS_LABELS[item.status] || item.status}
                        </strong>
                        <span>{formatFollowUpDate(item.sentAt || item.createdAt)}</span>
                      </div>
                      {item.subjectSnapshot ? (
                        <p>Subject: {item.subjectSnapshot}</p>
                      ) : null}
                      {item.errorMessage ? <p>Error: {item.errorMessage}</p> : null}
                    </article>
                  ))}
                </section>
              </section>
            ) : null}
          </>
        )}
      </section>
    </main>
  );
}
